import React, { useState, useEffect } from 'react';
import { X, Trophy, TrendingUp, Calendar, Gift, ChevronDown, Users, ChevronUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { multiNivelService, RankingMensalResponse } from '@/services/multi-nivel.service';

interface RankingProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RankingDetalhadoResponse {
  indicador: {
    id: number;
    nome: string;
    role: string;
    codigo: number;
    email: string;
  };
  mes: number;
  ano: number;
  total_indicacoes: number;
  indicacoes: Array<{
    id: number;
    data_indicacao: string;
    indicado: {
      id: number;
      nome: string;
      role: string;
      codigo: number;
      email: string;
    };
    ultimas_movimentacoes: Array<{
      id: number;
      valor: number;
      data: string;
      codigo: string;
    }>;
  }>;
}

export const Ranking: React.FC<RankingProps> = ({ isOpen, onClose }) => {
  const [rankingData, setRankingData] = useState<RankingMensalResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('atual');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDetalhado, setShowDetalhado] = useState(false);
  const [detalhadoLoading, setDetalhadoLoading] = useState(false);
  const [detalhadoData, setDetalhadoData] = useState<RankingDetalhadoResponse | null>(null);
  const [expandedIndicacoes, setExpandedIndicacoes] = useState<number[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mostrarApenasAtivas, setMostrarApenasAtivas] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string | null>(null);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [tipProgress, setTipProgress] = useState(0);
  const { toast } = useToast();

  const ranking_tips = [
    {
      title: "Valor Mínimo de Ativação",
      message: "Para que a indicação seja válida no ranking, a ativação mínima é de R$100 para clientes e R$500 para revendedoras."
    },
    {
      title: "Encerramento do Ranking",
      message: "O ranking é encerrado no último dia de cada mês, às 20h (horário de Brasília)."
    },
    {
      title: "Links de Indicação",
      message: "Você pode encontrar seus links de indicação na opção 'Minha Rede' no menu."
    }
  ];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  // Verificar se é admin
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userlogged') || '{}');
    setIsAdmin(userData.role === 'admin' || userData.role === 'administrador');
  }, []);

  // Controlar o progresso das dicas durante o loading
  useEffect(() => {
    if (!loading) return;

    const tipDuration = 12000; // 12 segundos por dica
    const updateInterval = 50; // Atualizar a cada 50ms para suavidade

    const interval = setInterval(() => {
      setTipProgress(prev => {
        if (prev >= 100) {
          // Avançar para próxima dica
          setCurrentTipIndex(index => (index + 1) % ranking_tips.length);
          return 0;
        }
        return prev + (updateInterval / tipDuration) * 100;
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [loading, ranking_tips.length]);

  // Gerar opções de períodos (últimos 3 meses, sem retroceder antes de novembro de 2025)
  const generatePeriodOptions = () => {
    const options = [{ value: 'atual', label: 'Mês Atual' }];
    const minDate = new Date(2025, 10, 1); // Novembro de 2025 (mês 10 = novembro)

    for (let i = 1; i <= 3; i++) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      
      // Para se a data for anterior a novembro de 2025
      if (date < minDate) {
        break;
      }
      
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthName = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

      options.push({
        value: `${year}-${month}`,
        label: monthName.charAt(0).toUpperCase() + monthName.slice(1)
      });
    }

    return options;
  };

  const fetchRanking = async (period: string) => {
    // Se for mês atual, tenta usar cache primeiro
    if (period === 'atual' && !isUltimoDiaMes()) {
      const cache = obterCacheRanking();
      if (cache) {
        setRankingData(cache.data);
        setUltimaAtualizacao(cache.date);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      let data: RankingMensalResponse;

      if (period === 'atual') {
        data = await multiNivelService.getRankingMensalAtual();
      } else {
        const [year, month] = period.split('-').map(Number);
        data = await multiNivelService.getRankingMensalPorPeriodo(year, month);
      }

      setRankingData(data);
      
      // Salva em cache se for mês atual e não for último dia
      if (period === 'atual') {
        salvarCacheRanking(data);
      }
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o ranking.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRanking(selectedPeriod);
    }
  }, [isOpen, selectedPeriod]);

  const isUltimoDiaMes = () => {
  
    const proximoDia = new Date(currentYear, currentMonth, 1);
    return new Date().toDateString() === new Date(proximoDia.getTime() - 1000 * 60 * 60 * 24).toDateString();
  };

  const obterCacheRanking = () => {
    try {
      const cache = localStorage.getItem('rankingCache');
      const dataCacheTimestamp = localStorage.getItem('rankingCacheDate');
      const dataCacheFormatada = localStorage.getItem('rankingCacheDateFormatada');
      
      if (cache && dataCacheTimestamp && dataCacheFormatada) {
        // Verifica se o cache tem mais de 12 horas
        const dataCacheTime = parseInt(dataCacheTimestamp, 10);
        const agora = new Date().getTime();
        const diferenca = agora - dataCacheTime;
        const doze_horas = 12 * 60 * 60 * 1000; // 12 horas em milissegundos
        if (diferenca > doze_horas) {
          // Cache expirado, retorna null para forçar nova busca
          return null;
        }
        
        return { data: JSON.parse(cache), date: dataCacheFormatada };
      }
    } catch (error) {
      console.error('Erro ao obter cache do ranking:', error);
    }
    return null;
  };

  const salvarCacheRanking = (data: RankingMensalResponse) => {
    if (!isUltimoDiaMes()) {
      try {
        const agora = new Date();
        const dataFormatada = agora.toLocaleString('pt-BR');
        const timestamp = agora.getTime();
        
        localStorage.setItem('rankingCache', JSON.stringify(data));
        localStorage.setItem('rankingCacheDate', timestamp.toString());
        localStorage.setItem('rankingCacheDateFormatada', dataFormatada);
        setUltimaAtualizacao(dataFormatada);
      } catch (error) {
        console.error('Erro ao salvar cache do ranking:', error);
      }
    }
  };

  const limparCacheRanking = () => {
    try {
      localStorage.removeItem('rankingCache');
      localStorage.removeItem('rankingCacheDate');
      localStorage.removeItem('rankingCacheDateFormatada');
      setUltimaAtualizacao(null);
    } catch (error) {
      console.error('Erro ao limpar cache do ranking:', error);
    }
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
  };

  // Fetch ranking detalhado
  const fetchRankingDetalhado = async (indicadoraId: number, period: string) => {
    setDetalhadoLoading(true);
    try {
      let data: RankingDetalhadoResponse;

      if (period === 'atual') {
        data = await multiNivelService.getRankingDetalhado(indicadoraId);
      } else {
        const [year, month] = period.split('-').map(Number);
        data = await multiNivelService.getRankingDetalhadoPorPeriodo(
          indicadoraId,
          year,
          month
        );
      }

      setDetalhadoData(data);
      setExpandedIndicacoes([]);
    } catch (error) {
      console.error('Erro ao carregar ranking detalhado:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o ranking detalhado.",
        variant: "destructive",
      });
    } finally {
      setDetalhadoLoading(false);
    }
  };

  const handleOpenDetalhado = (indicadorId: number) => {
    setShowDetalhado(true);
    fetchRankingDetalhado(indicadorId, selectedPeriod);
  };

  const toggleIndicacao = (indicacaoId: number) => {
    setExpandedIndicacoes(prev =>
      prev.includes(indicacaoId)
        ? prev.filter(id => id !== indicacaoId)
        : [...prev, indicacaoId]
    );
  };

  const formatarValor = (valor: number) => {
    return `R$ ${(valor / 100).toFixed(2)}`;
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const ordenarIndicacoesPorData = (indicacoes: RankingDetalhadoResponse['indicacoes']) => {
    return [...indicacoes].sort((a, b) => {
      return new Date(b.data_indicacao).getTime() - new Date(a.data_indicacao).getTime();
    });
  };

  const contarMovimentacoesNoMes = (movimentacoes: Array<{ id: number; valor: number; data: string; codigo: string }>, mesReferencia: number, anoReferencia: number) => {
    return movimentacoes.filter(mov => {
      const dataMov = new Date(mov.data);
      return dataMov.getMonth() + 1 === mesReferencia && dataMov.getFullYear() === anoReferencia;
    }).length;
  };

  const calcularSomaMovimentacoesNoMes = (movimentacoes: Array<{ id: number; valor: number; data: string; codigo: string }>, mesReferencia: number, anoReferencia: number) => {
    return movimentacoes.filter(mov => {
      const dataMov = new Date(mov.data);
      return dataMov.getMonth() + 1 === mesReferencia && dataMov.getFullYear() === anoReferencia;
    }).reduce((soma, mov) => soma + mov.valor, 0);
  };

  const verificarAtingiunMeta = (somaMovimentacoes: number, role: string): boolean => {
    if (role === 'cliente') {
      return somaMovimentacoes >= 10000; // R$ 100,00 em centavos
    } else if (role === 'revendedora') {
      return somaMovimentacoes >= 50000; // R$ 500,00 em centavos
    }
    return false;
  };

  const calcularSomaMovimentacoesAposIndicacao = (movimentacoes: Array<{ id: number; valor: number; data: string; codigo: string }>, dataIndicacao: string, mesReferencia: number, anoReferencia: number): number => {
    const dataIndic = new Date(dataIndicacao);
    return movimentacoes.filter(mov => {
      const dataMov = new Date(mov.data);
      // Apenas movimentações após a indicação e no mês de referência
      return dataMov >= dataIndic && 
             dataMov.getMonth() + 1 === mesReferencia && 
             dataMov.getFullYear() === anoReferencia;
    }).reduce((soma, mov) => soma + mov.valor, 0);
  };

  const jaAtingiunMetaEmMesAnterior = (indicacao: RankingDetalhadoResponse['indicacoes'][0], mesReferencia: number, anoReferencia: number): boolean => {
    const dataIndicacao = new Date(indicacao.data_indicacao);
    const mesIndicacao = dataIndicacao.getMonth() + 1;
    const anoIndicacao = dataIndicacao.getFullYear();
    
    // Se a indicação foi feita neste mês, não pode ter atingido em mês anterior
    if (mesIndicacao === mesReferencia && anoIndicacao === anoReferencia) {
      return false;
    }
    
    // Se a indicação foi feita em mês anterior, verifica se já atingiu a meta
    if (anoIndicacao < anoReferencia || (anoIndicacao === anoReferencia && mesIndicacao < mesReferencia)) {
      const somaAnterior = calcularSomaMovimentacoesAposIndicacao(
        indicacao.ultimas_movimentacoes,
        indicacao.data_indicacao,
        mesIndicacao === 1 ? 12 : mesIndicacao - 1,
        mesIndicacao === 1 ? anoIndicacao - 1 : anoIndicacao
      );
      return verificarAtingiunMeta(somaAnterior, indicacao.indicado.role);
    }
    
    return false;
  };

  const contarAtivacoesMes = (indicacoes: RankingDetalhadoResponse['indicacoes'], mesReferencia: number, anoReferencia: number): number => {
    return indicacoes.filter(indicacao => {
      // Verifica se já tem movimentações em mês anterior - se sim, descarta
      // (não importa se atingiu ou não a meta em mês anterior, qualquer movimento anterior a descarta)
      if (jaTemMovimentacoesEmMesAnterior(indicacao, mesReferencia, anoReferencia)) {
        return false;
      }
      
      // Calcula soma de movimentações após a indicação, apenas do mês de referência
      const soma = calcularSomaMovimentacoesAposIndicacao(
        indicacao.ultimas_movimentacoes,
        indicacao.data_indicacao,
        mesReferencia,
        anoReferencia
      );
      
      // Verifica se atingiu a meta neste mês
      return verificarAtingiunMeta(soma, indicacao.indicado.role);
    }).length;
  };

  const jaTemMovimentacoesEmMesAnterior = (indicacao: RankingDetalhadoResponse['indicacoes'][0], mesReferencia: number, anoReferencia: number): boolean => {
    const dataIndicacao = new Date(indicacao.data_indicacao);
    
    // Verifica se há movimentações que:
    // 1. Ocorreram APÓS a data de indicação (para descartar movimentações antes da indicação)
    // 2. Ocorreram em mês ANTERIOR ao mês de referência (para verificar se já movimentou antes)
    return indicacao.ultimas_movimentacoes.some(mov => {
      const dataMov = new Date(mov.data);
      
      // Movimento deve ser após a indicação
      if (dataMov < dataIndicacao) {
        return false;
      }
      
      // Verifica se é anterior ao mês de referência
      const mesMovimento = dataMov.getMonth() + 1;
      const anoMovimento = dataMov.getFullYear();
      
      // Movimento em ano anterior é considerado "anterior"
      if (anoMovimento < anoReferencia) {
        return true;
      }
      
      // Movimento no mesmo ano, mas mês anterior
      if (anoMovimento === anoReferencia && mesMovimento < mesReferencia) {
        return true;
      }
      
      return false;
    });
  };

  const getRankingColor = (position: number) => {
    switch (position) {
      case 1:
        return 'text-yellow-600 bg-yellow-50';
      case 2:
        return 'text-gray-600 bg-gray-50';
      case 3:
        return 'text-amber-600 bg-amber-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  const getRankingIcon = (position: number) => {
    if (position <= 3) {
      return <Trophy className="h-4 w-4" />;
    }
    // return <TrendingUp className="h-4 w-4" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end lg:items-center lg:justify-center">
      {/* Bottom Sheet Container */}
      <div className="bg-background w-full lg:w-full lg:max-w-2xl lg:mx-auto rounded-t-3xl lg:rounded-3xl shadow-xl animate-in slide-in-from-bottom duration-300 lg:animate-in lg:fade-in lg:zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              Ranking de Indicações
            </h2>
            <p className="text-sm text-muted-foreground">Veja sua posição no ranking mensal</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[70vh] lg:max-h-[calc(85vh-180px)] overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Selecione o período" />
                  </SelectTrigger>
                  <SelectContent>
                    {generatePeriodOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Botão Ver Premiação */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 font-normal">
                    <Gift className="h-4 w-4" />
                    Ver Premiação
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                      🥇 Top 1
                    </Badge>
                  </div>
                  <span className="font-semibold">30 diamantes</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                      🥈 Top 2
                    </Badge>
                  </div>
                  <span className="font-semibold">20 diamantes</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                      🥉 Top 3
                    </Badge>
                  </div>
                  <span className="font-semibold">10 diamantes</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>

            {/* Update Info and Refresh Button */}
            {selectedPeriod === 'atual' && !isUltimoDiaMes() && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  {ultimaAtualizacao ? (
                    <span className="text-blue-700">
                      Atualizado em: <span className="font-semibold">{ultimaAtualizacao}</span>
                    </span>
                  ) : (
                    <span className="text-blue-700">Atualizando ranking, aguarde...</span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    limparCacheRanking();
                    fetchRanking('atual');
                  }}
                  className="gap-2 text-xs"
                  title="Atualizar ranking"
                >
                  🔄 Atualizar
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-full max-w-md space-y-6">
                {/* Loading Spinner */}
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                  <p className="text-lg font-semibold text-foreground">Carregando Ranking</p>
                </div>

                {/* Tips Section */}
                <div className="space-y-4">                  
                  {/* Tips Carousel */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-5 min-h-32 flex flex-col justify-between">
                    {/* Tip Content */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-muted-foreground px-2">💡 Dicas:</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{ranking_tips[currentTipIndex].message}</p>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-1 bg-blue-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-75"
                        style={{ width: `${tipProgress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Navigation and Indicators */}
                  <div className="flex items-center justify-between px-2">
                    {/* Left Arrow Button */}
                    <button
                      onClick={() => {
                        setCurrentTipIndex(prev => (prev - 1 + ranking_tips.length) % ranking_tips.length);
                        setTipProgress(0);
                      }}
                      className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                      title="Dica anterior"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    {/* Indicators */}
                    <div className="flex gap-2">
                      {ranking_tips.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setCurrentTipIndex(index);
                            setTipProgress(0);
                          }}
                          className={`h-2 rounded-full transition-all ${
                            index === currentTipIndex
                              ? 'w-6 bg-primary'
                              : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                          }`}
                          title={`Dica ${index + 1}`}
                        ></button>
                      ))}
                    </div>

                    {/* Right Arrow Button */}
                    <button
                      onClick={() => {
                        setCurrentTipIndex(prev => (prev + 1) % ranking_tips.length);
                        setTipProgress(0);
                      }}
                      className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                      title="Próxima dica"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : rankingData ? (
            <div className="space-y-4">
              {/* Period Info */}
              {/* <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Período de Referência</p>
                    <p className="font-semibold text-foreground">
                      {rankingData.periodo_inicio} - {rankingData.periodo_fim}
                    </p>
                  </div>
                </CardContent>
              </Card> */}

              {/* My Position */}
              {rankingData.ranking.length > 0 && (
                <Card className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-primary">Sua Posição</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(() => {
                      // Encontrar posição do usuário atual no ranking
                      const userData = JSON.parse(localStorage.getItem('userlogged') || '{}');
                      const myPosition = rankingData.ranking.find(user =>
                        user.codigo == userData.codigo
                      );

                      return myPosition ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className={getRankingColor(myPosition.posicao)}>
                              {getRankingIcon(myPosition.posicao)}
                              #{myPosition.posicao}
                            </Badge>
                            <div>
                              <p className="text-xs text-muted-foreground">#{myPosition.codigo}</p>
                              <span className="font-medium text-foreground">{myPosition.nome}</span>
                             
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-primary">
                            {myPosition.indicacoes_nivel_1} indicações
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Você ainda não aparece no ranking deste período.
                        </p>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Ranking List */}
              {rankingData.ranking.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Indicadores</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {rankingData.ranking.map((user, index) => (
                        <div
                          key={user.posicao}
                          className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <Badge
                              className={getRankingColor(user.posicao)}
                              variant="secondary"
                            >
                              {getRankingIcon(user.posicao)}
                              #{user.posicao}
                            </Badge>
                            <div>
                              <p className="text-xs text-muted-foreground">#{user.codigo}</p>
                              <p className="font-medium text-foreground">{user.nome}</p>
                              {user.posicao <= 3 && (
                                <p className="text-xs text-muted-foreground">
                                  {user.posicao === 1 ? '🥇 Primeiro lugar' :
                                    user.posicao === 2 ? '🥈 Segundo lugar' :
                                      '🥉 Terceiro lugar'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-foreground">
                                {user.indicacoes_nivel_1}
                              </p>
                              <p className="text-xs text-muted-foreground">indicações</p>
                            </div>
                            {isAdmin && (
                              <Button 
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenDetalhado(parseInt(user.codigo.toString()))}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Ver detalhes"
                              >
                                <TrendingDown className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}


              {rankingData.ranking.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Ainda não há dados de ranking para este período.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  Não foi possível carregar os dados do ranking.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer - apenas no mobile */}
        <div className="p-6 border-t lg:hidden flex gap-2">
          <Button
            onClick={() => setShowInfoModal(true)}
            variant="outline"
            className="flex-1"
          >
            Saiba mais
          </Button>
          <Button
            onClick={onClose}
            variant="default"
            className="flex-1"
          >
            Fechar
          </Button>
        </div>
      </div>

      {/* Modal Ranking Detalhado */}
      {showDetalhado && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end lg:items-center lg:justify-center p-4">
          <div className="bg-background w-full lg:max-w-3xl rounded-t-3xl lg:rounded-3xl shadow-xl animate-in slide-in-from-bottom duration-300 lg:animate-in lg:fade-in lg:zoom-in-95 max-h-[85vh] lg:max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Indicações Detalhado
                </h2>
                {detalhadoData && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {detalhadoData.indicador.nome} • {detalhadoData.total_indicacoes} indicações
                    </p>
                    <p className="text-xs text-green-700 bg-green-50 inline-block px-2 py-1 rounded">
                      ✓ {contarAtivacoesMes(detalhadoData.indicacoes, detalhadoData.mes, detalhadoData.ano)} ativações neste mês
                    </p>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDetalhado(false)}
                className="hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {detalhadoLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : detalhadoData && detalhadoData.indicacoes.length > 0 ? (
                <div className="space-y-3">
                  {/* Info sobre limite */}
                  <div className='flex items-center justify-between gap-3'>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1">
                      <p className="text-xs text-blue-900">
                        <strong>ℹ️ Nota:</strong> Mostrando as últimas 5 movimentações por indicado. Para ver o histórico completo, acesse os recibos.
                      </p>
                    </div>
                    <button
                      onClick={() => setMostrarApenasAtivas(!mostrarApenasAtivas)}
                      className={`px-3 py-2 rounded-lg border transition-all whitespace-nowrap text-xs font-medium ${
                        mostrarApenasAtivas
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : 'bg-gray-100 border-gray-300 text-gray-800'
                      }`}
                      title="Mostrar apenas indicações com meta atingida"
                    >
                      {mostrarApenasAtivas ? '✓ Apenas Ativas' : 'Todas'}
                    </button>
                  </div>

                 
                  {ordenarIndicacoesPorData(detalhadoData.indicacoes).filter((indicacao) => {
                    if (!mostrarApenasAtivas) return true;
                    const somaMovimentacoes = calcularSomaMovimentacoesNoMes(indicacao.ultimas_movimentacoes, detalhadoData.mes, detalhadoData.ano);
                    const atingiuMeta = verificarAtingiunMeta(somaMovimentacoes, indicacao.indicado.role);
                    const jaTemMovimentacoes = jaTemMovimentacoesEmMesAnterior(indicacao, detalhadoData.mes, detalhadoData.ano);
                    return atingiuMeta && !jaTemMovimentacoes;
                  }).map((indicacao) => {
                    const somaMovimentacoes = calcularSomaMovimentacoesNoMes(indicacao.ultimas_movimentacoes, detalhadoData.mes, detalhadoData.ano);
                    const atingiuMeta = verificarAtingiunMeta(somaMovimentacoes, indicacao.indicado.role);
                    const jaTemMovimentacoes = jaTemMovimentacoesEmMesAnterior(indicacao, detalhadoData.mes, detalhadoData.ano);
                    const podeSerConsideradaAtiva = atingiuMeta && !jaTemMovimentacoes;
                    
                    return (
                    <Card key={indicacao.id} className={`overflow-hidden transition-colors ${podeSerConsideradaAtiva ? 'bg-green-50 border-green-200' : ''}`}>
                      <button
                        onClick={() => toggleIndicacao(indicacao.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 text-left">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple/20 to-blue/20 flex items-center justify-center">
                              <span className="text-sm font-semibold text-primary">
                                #{indicacao.indicado.codigo}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {indicacao.indicado.nome}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {indicacao.indicado.role === 'cliente' ? '👤 Cliente' : '🏪 Revendedora'} • Indicado em {formatarData(indicacao.data_indicacao)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {detalhadoData ? contarMovimentacoesNoMes(indicacao.ultimas_movimentacoes, detalhadoData.mes, detalhadoData.ano) : 0} movimentação(ões) neste mês • {formatarValor(somaMovimentacoes)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {detalhadoData && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${atingiuMeta && !jaTemMovimentacoesEmMesAnterior(indicacao, detalhadoData.mes, detalhadoData.ano) ? 'bg-green-100 border-green-300 text-green-800' : ''}`}
                              title={`${atingiuMeta && !jaTemMovimentacoesEmMesAnterior(indicacao, detalhadoData.mes, detalhadoData.ano) ? '✓ Meta atingida!' : 'Meta não atingida'}`}
                            >
                              {(() => {
                                return indicacao.ultimas_movimentacoes.length > 0 ? `${indicacao.ultimas_movimentacoes.length} compras/vendas` : '0 movimentações';
                              })()}
                            </Badge>
                          )}
                          {expandedIndicacoes.includes(indicacao.id) ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </button>

                      {/* Conteúdo Colapsável */}
                      {expandedIndicacoes.includes(indicacao.id) && (
                        <div className="border-t p-4 bg-muted/30 space-y-3">
                          {indicacao.ultimas_movimentacoes.length > 0 ? (
                            <>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Últimas movimentações
                              </p>
                              <div className="space-y-2">
                                {indicacao.ultimas_movimentacoes.map((mov) => (
                                  <div
                                    key={mov.id}
                                    className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/50"
                                  >
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-foreground">
                                        {mov.codigo}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {formatarData(mov.data)}
                                      </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-sm font-semibold text-green-600">
                                        {formatarValor(mov.valor)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-6">
                              <TrendingDown className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                              <p className="text-sm text-muted-foreground">
                                Nenhuma movimentação registrada
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                    );
                  })}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">
                      Nenhuma indicação encontrada para este período.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex-shrink-0">
              <Button
                onClick={() => setShowDetalhado(false)}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Informações */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden">
            {/* Header da Modal */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-foreground">Sobre o Ranking</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInfoModal(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Conteúdo da Modal */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <h4 className="font-semibold text-foreground mb-2">1. O que é o Ranking de Indicações?</h4>
                <p className="text-sm text-muted-foreground">
                  É uma classificação mensal que mostra quem mais indicou novas clientes e revendedoras para a X do Infinito e que se ativaram dentro do período do ranking.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">2. Qual é o valor mínimo de ativação?</h4>
                <p className="text-sm text-muted-foreground">
                  Para que a indicação seja válida no ranking, a ativação mínima é de R$100,00 para clientes e R$500,00 para revendedoras.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">3. Como funciona o critério de desempate?</h4>
                <p className="text-sm text-muted-foreground">
                  Se duas pessoas tiverem o mesmo número de indicações, vence quem cadastrou primeiro a indicada que atingiu a quantidade do empate no ranking.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">4. Quais são os prêmios do ranking?</h4>
                <p className="text-sm text-muted-foreground">
                  Os prêmios são concedidos em diamantes, que ficam disponíveis na sua conta para acumular e depois trocar por recompensas na Loja de Recompensas.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">5. Onde posso ver minha posição?</h4>
                <p className="text-sm text-muted-foreground">
                  No painel do ranking você consegue visualizar sua posição no mês atual e acompanhar os principais indicadores.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-2">6. Quando o ranking é fechado?</h4>
                <p className="text-sm text-muted-foreground">
                  O ranking é encerrado no último dia de cada mês, às 20h (horário de Brasília). O resultado final fica disponível até às 21h (horário de Brasília) do mesmo dia, já considerando todas as indicações validadas dentro do período
                </p>
              </div>
            </div>

            {/* Footer da Modal */}
            <div className="p-4 border-t">
              <Button
                onClick={() => setShowInfoModal(false)}
                className="w-full"
              >
                Entendi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ranking;
